/**
 * MM Admin Finance — audit evidence for sponsor treasury and MyMoolah own income.
 * Partner settlement stays on /admin/partner-settlement.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileSearch, Landmark, Scale, Shield } from 'lucide-react';
import { KpiMetricCard } from '../dashboard/KpiMetricCard';
import { currentSastPeriod, MonthPeriodMenu } from './MonthPeriodMenu';

const API_BASE = '/api/v1/admin/finance';

interface CatalogueRow {
  id: string;
  audience: string;
  title: string;
  status: string;
  screen?: string;
  note?: string;
}

interface FloatRow {
  code: string;
  name: string;
  ledgerCents: number;
  operationalCents: number | null;
  differenceCents: number | null;
  status?: string;
}

interface CountRow {
  status: string;
  count: number;
}

interface PolicyRow {
  id: string;
  title: string;
  owner: string;
  frameworkVersion: string;
  effective: string;
  status: string;
}

interface TreasuryPack {
  sponsorId: string;
  emptyReason?: string;
  treasury: { code: string; name: string | null; balanceCents: number | null } | null;
  statement: {
    statementDate: string;
    statementType?: string | null;
    closingBalanceCents: number;
    storedAt?: string | null;
    interim?: boolean;
  } | null;
  treasuryFileStatus?: string;
  treasuryStatementDifferenceCents: number | null;
  doubleEntryUnbalancedCount: number | null;
  walletCents: number | null;
  walletFloatCents: number | null;
  restrictedFloatCents?: number | null;
  walletFloatDifferenceCents: number | null;
  walletFloatStatus?: string;
  suspenseCents: number | null;
  supplierFloats: FloatRow[];
  reconBreaks: CountRow[];
  kyc: { rows: CountRow[] };
  kyb: { rows: CountRow[] };
  fic: { rows: CountRow[] };
  policies: PolicyRow[];
  catalogue: CatalogueRow[];
}

interface IncomeLine {
  code: string;
  name: string;
  movementCents: number;
}

interface CompanyBooks {
  entity: { name: string; registration: string };
  income: {
    revenue: IncomeLine[];
    contra: IncomeLine[];
    revenueCents: number;
    contraCents: number;
    netOwnIncomeCents: number;
  };
  expenses: IncomeLine[];
  vatOutputCents: number | null;
  operatingAccount: { code: string; balanceCents: number | null };
  marginSweepCents: number | null;
  salaryActivity: string;
  rentActivity: string;
}

interface PopulationSummary {
  id: string;
  label: string;
  source: string;
  count: number;
  amountCents: number;
  failedCount: number;
  activity: string;
  unavailable: boolean;
}

interface PopulationDetail extends PopulationSummary {
  rows: Array<{
    reference: string;
    source: string;
    status: string;
    amountCents: number;
    occurredAt: string;
  }>;
}

const STATUS_LABEL: Record<string, string> = {
  from_ledger: 'From the ledger',
  existing_screen: 'Existing screen',
  policy_on_file: 'Policy on file',
  not_lodged: 'Not lodged',
  not_applicable: 'Not applicable',
  pass: 'Pass',
  explained: 'Explained',
  break: 'Break',
  receivable: 'Receivable',
  no_activity: 'No activity',
};

function authHeaders(): HeadersInit {
  const token = sessionStorage.getItem('portal_token');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function evidenceStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return STATUS_LABEL[status] || status;
}

function formatRand(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(cents / 100);
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export const AuditEvidenceOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(currentSastPeriod);
  const [sponsor, setSponsor] = useState<'sbsa' | 'nedbank'>('sbsa');
  const [pack, setPack] = useState<TreasuryPack | null>(null);
  const [books, setBooks] = useState<CompanyBooks | null>(null);
  const [populations, setPopulations] = useState<PopulationSummary[]>([]);
  const [detail, setDetail] = useState<PopulationDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const query = period ? `period=${encodeURIComponent(period)}` : '';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setDetail(null);
    try {
      const suffix = query ? `?${query}` : '';
      const sponsorQuery = `${suffix}${suffix ? '&' : '?'}sponsor=${sponsor}`;
      const [treasuryRes, companyRes] = await Promise.all([
        fetch(`${API_BASE}/treasury${sponsorQuery}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/company${suffix}`, { headers: authHeaders() }),
      ]);
      if (!treasuryRes.ok || !companyRes.ok) {
        setError('Audit evidence could not be loaded.');
        return;
      }
      const treasuryJson = await treasuryRes.json();
      const companyJson = await companyRes.json();
      setPack(treasuryJson.data);
      setBooks(companyJson.data);
      if (sponsor === 'nedbank') {
        setPopulations([]);
        return;
      }
      const populationRes = await fetch(`${API_BASE}/populations${suffix}`, { headers: authHeaders() });
      if (!populationRes.ok) {
        setError('Audit evidence could not be loaded.');
        return;
      }
      const populationJson = await populationRes.json();
      setPopulations(populationJson.data.rows || []);
    } catch {
      setError('Audit evidence could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [query, sponsor]);

  useEffect(() => {
    load();
  }, [load]);

  async function openPopulation(id: string) {
    const suffix = query ? `&${query}` : '';
    const res = await fetch(`${API_BASE}/populations?type=${encodeURIComponent(id)}${suffix}`, { headers: authHeaders() });
    if (!res.ok) return;
    const json = await res.json();
    setDetail(json.data);
    scrollToId('population-detail');
  }

  async function download(format: 'pdf' | 'xls') {
    const suffix = query ? `&${query}` : '';
    const res = await fetch(`${API_BASE}/download?format=${format}&sponsor=${sponsor}${suffix}`, { headers: authHeaders() });
    if (!res.ok) {
      setError('The evidence pack could not be downloaded.');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-evidence-${sponsor}${period ? `-${period}` : ''}.${format}`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--foreground)]">Audit evidence</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--muted-foreground)]">
            INTERNAL. Audit evidence. Not an audit opinion. Not for channel partners.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonthPeriodMenu idPrefix="audit-period" value={period} onChange={setPeriod} />
          <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => download('pdf')}>
            <Download className="mr-1 inline h-4 w-4" /> PDF
          </button>
          <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => download('xls')}>
            <Download className="mr-1 inline h-4 w-4" /> Spreadsheet
          </button>
        </div>
      </div>

      {error ? <p className="text-sm text-red-700">{error}</p> : null}
      {loading ? <p className="text-sm text-[var(--muted-foreground)]">Loading evidence…</p> : null}

      <section className="mymoolah-card space-y-4 p-4">
        <div className="flex gap-2">
          <button type="button" className={`rounded-md px-3 py-2 text-sm ${sponsor === 'sbsa' ? 'bg-[var(--primary)] text-white' : 'border'}`} onClick={() => setSponsor('sbsa')}>Standard Bank</button>
          <button type="button" className={`rounded-md px-3 py-2 text-sm ${sponsor === 'nedbank' ? 'bg-[var(--primary)] text-white' : 'border'}`} onClick={() => setSponsor('nedbank')}>Nedbank</button>
        </div>
        {pack?.emptyReason ? (
          <p className="text-sm">This sponsor has no sponsor treasury account. Standard Bank figures are not shown here.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <KpiMetricCard label="Treasury" accent="blue" icon={Landmark} onClick={() => scrollToId('safeguarding')} metrics={[{ value: formatRand(pack?.treasury?.balanceCents), label: pack?.treasury?.code || '' }]} />
            <KpiMetricCard label="Wallet versus float" accent={pack?.walletFloatStatus === 'break' ? 'orange' : 'green'} icon={Scale} onClick={() => scrollToId('safeguarding')} metrics={[{ value: formatRand(pack?.walletFloatDifferenceCents), label: evidenceStatus(pack?.walletFloatStatus) }]} />
            <KpiMetricCard label="Unbalanced journals" accent="orange" icon={FileSearch} onClick={() => scrollToId('safeguarding')} metrics={[{ value: String(pack?.doubleEntryUnbalancedCount ?? '—'), label: 'count' }]} />
            <KpiMetricCard label="Suspense" accent="purple" icon={Shield} onClick={() => scrollToId('safeguarding')} metrics={[{ value: formatRand(pack?.suspenseCents), label: '2600-01-01' }]} />
          </div>
        )}
      </section>

      {!pack?.emptyReason ? (
        <section id="safeguarding" className="mymoolah-card space-y-3 p-4">
          <h2 className="text-lg font-medium">Safeguarding</h2>
          <p className="text-sm">Treasury ledger {formatRand(pack?.treasury?.balanceCents)}.</p>
          <p className="text-sm">
            Statement file {pack?.statement?.statementType || '—'}
            {pack?.statement?.interim ? ' (interim)' : ''}
            {' '}closing {formatRand(pack?.statement?.closingBalanceCents)}
            {pack?.statement?.storedAt ? `, stored ${pack.statement.storedAt}` : ''}.
            {' '}File status {evidenceStatus(pack?.treasuryFileStatus)}.
            {pack?.treasuryFileStatus === 'explained'
              ? ' The file is behind the ledger. This is not a cash difference.'
              : ''}
          </p>
          <p className="text-sm">
            Wallet total {formatRand(pack?.walletCents)}. Client-float liability {formatRand(pack?.walletFloatCents)}.
            Restricted {formatRand(pack?.restrictedFloatCents)}.
            Status {evidenceStatus(pack?.walletFloatStatus)}. Difference {formatRand(pack?.walletFloatDifferenceCents)}.
          </p>
          <table className="w-full text-left text-sm">
            <thead>
              <tr><th>Float</th><th>Ledger</th><th>Operational</th><th>Difference</th><th>Status</th></tr>
            </thead>
            <tbody>
              {(pack?.supplierFloats || []).map((row) => (
                <tr key={row.code}>
                  <td>{row.code}</td>
                  <td>{formatRand(row.ledgerCents)}</td>
                  <td>{formatRand(row.operationalCents)}</td>
                  <td>{formatRand(row.differenceCents)}</td>
                  <td>{evidenceStatus(row.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(pack?.supplierFloats || []).length === 0 ? <p className="text-sm">no activity</p> : null}
          <p className="text-sm">Recon breaks stay on the existing recon API. Open counts: {(pack?.reconBreaks || []).map((row) => `${row.status} ${row.count}`).join(', ') || 'no activity'}.</p>
        </section>
      ) : null}

      <section className="mymoolah-card space-y-3 p-4">
        <h2 className="text-lg font-medium">Transaction populations</h2>
        {pack?.emptyReason ? <p className="text-sm">no activity</p> : (
        <>
        <table className="w-full text-left text-sm">
          <thead>
            <tr><th>Type</th><th>Count</th><th>Value</th><th>Failed</th></tr>
          </thead>
          <tbody>
            {populations.map((row) => (
              <tr key={row.id}>
                <td>
                  <button type="button" className="underline" onClick={() => openPopulation(row.id)}>{row.label}</button>
                </td>
                <td>{row.activity === 'no activity' ? 'no activity' : row.count}</td>
                <td>{row.activity === 'no activity' ? 'no activity' : formatRand(row.amountCents)}</td>
                <td>{row.failedCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {detail ? (
          <div id="population-detail">
            <h3 className="font-medium">{detail.label}</h3>
            <p className="text-sm text-[var(--muted-foreground)]">{detail.source}</p>
            {detail.rows.length === 0 ? <p className="text-sm">no activity</p> : (
              <table className="w-full text-left text-sm">
                <thead><tr><th>Reference</th><th>Status</th><th>Amount</th></tr></thead>
                <tbody>
                  {detail.rows.map((row) => (
                    <tr key={`${row.source}-${row.reference}`}>
                      <td>{row.reference}</td>
                      <td>{row.status}</td>
                      <td>{formatRand(row.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : null}
        </>
        )}
      </section>

      {!pack?.emptyReason ? (
        <section className="grid gap-4 md:grid-cols-3">
          <CountBlock title="KYC" rows={pack?.kyc.rows || []} onOpen={() => navigate('/admin/users')} />
          <CountBlock title="KYB" rows={pack?.kyb.rows || []} onOpen={() => navigate('/admin/channel-partners')} />
          <CountBlock title="Sanctions alerts" rows={pack?.fic.rows || []} onOpen={() => navigate('/admin/fic-tfs')} />
        </section>
      ) : null}

      <section className="mymoolah-card space-y-3 p-4">
        <h2 className="text-lg font-medium">MyMoolah own income</h2>
        <p className="text-sm">{books?.entity.name} {books?.entity.registration}</p>
        <p className="text-sm">This is outside client money. Face value and wallet balances are not income.</p>
        <div className="grid gap-4 md:grid-cols-3">
          <KpiMetricCard label="Own income" accent="green" icon={Scale} onClick={() => scrollToId('income-lines')} metrics={[{ value: formatRand(books?.income.netOwnIncomeCents), label: 'after contra' }]} />
          <KpiMetricCard label="VAT output" accent="purple" icon={Shield} onClick={() => scrollToId('income-lines')} metrics={[{ value: formatRand(books?.vatOutputCents), label: 'not profit' }]} footnote="Not added to income" />
          <KpiMetricCard label="Operating account" accent="blue" icon={Landmark} onClick={() => scrollToId('income-lines')} metrics={[{ value: formatRand(books?.operatingAccount.balanceCents), label: books?.operatingAccount.code || '' }]} />
        </div>
        <div id="income-lines" className="space-y-2 text-sm">
          <p>Revenue {formatRand(books?.income.revenueCents)}. Contra {formatRand(books?.income.contraCents)}. Margin sweep to the operating account {formatRand(books?.marginSweepCents)}.</p>
          {(books?.income.revenue || []).map((row) => <p key={row.code}>{row.code} {row.name}: {formatRand(row.movementCents)}</p>)}
          {(books?.income.contra || []).map((row) => <p key={row.code}>{row.code} {row.name}: {formatRand(row.movementCents)}</p>)}
          <p>Salaries: {books?.salaryActivity}. Rent: {books?.rentActivity}.</p>
          {(books?.expenses || []).length === 0 ? <p>Expenses: no activity</p> : books?.expenses.map((row) => <p key={row.code}>{row.code} {row.name}: {formatRand(row.movementCents)}</p>)}
        </div>
      </section>

      <section className="mymoolah-card space-y-3 p-4">
        <h2 className="text-lg font-medium">Request catalogue</h2>
        <table className="w-full text-left text-sm">
          <thead><tr><th>Audience</th><th>Evidence</th><th>Status</th></tr></thead>
          <tbody>
            {(pack?.catalogue || []).map((row) => (
              <tr key={row.id}>
                <td>{row.audience}</td>
                <td>{row.screen ? <button type="button" className="underline" onClick={() => navigate(row.screen as string)}>{row.title}</button> : row.title}</td>
                <td>{STATUS_LABEL[row.status] || row.status}{row.note ? ` — ${row.note}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mymoolah-card space-y-2 p-4">
        <h2 className="text-lg font-medium">Policies</h2>
        <table className="w-full text-left text-sm">
          <thead><tr><th>Policy</th><th>Title</th><th>Version</th><th>Effective</th></tr></thead>
          <tbody>
            {(pack?.policies || []).map((row) => (
              <tr key={row.id}><td>{row.id}</td><td>{row.title}</td><td>{row.frameworkVersion}</td><td>{row.effective}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

function CountBlock({ title, rows, onOpen }: { title: string; rows: CountRow[]; onOpen: () => void }) {
  return (
    <div className="mymoolah-card space-y-2 p-4">
      <button type="button" className="text-lg font-medium underline" onClick={onOpen}>{title}</button>
      {rows.length === 0 ? <p className="text-sm">no activity</p> : rows.map((row) => (
        <p key={row.status} className="text-sm">{row.status}: {row.count}</p>
      ))}
    </div>
  );
}
